import { useQuery } from "@tanstack/react-query";

import type { EventDetailsDto, EventDto } from "@/api/types";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { Badge } from "@/design-system/Badge";
import { getEventCategoryName } from "@/helpers/events";
import { useEventQueries } from "@/queries/events/queryOptions";
import { useTranslation } from "@/translations";

type EventBadgesProps = {
  event: EventDetailsDto | EventDto;
};

export function EventBadges({ event }: EventBadgesProps): React.ReactNode {
  const { t } = useTranslation();

  const eventQueries = useEventQueries();
  const { data: eventCategories = [] } = useQuery(eventQueries.listCategories());

  const categoryDetails = event.category ? eventCategories.find(({ id }) => id === event.category?.id) : undefined;

  return (
    <>
      {event.deletedAt && <Badge variant="danger">{t("model.event.details.deleted")}</Badge>}
      {!event.publishedAt && (
        <Badge variant="info">
          <span className="flex gap-space-xs whitespace-nowrap">
            {t("model.event.details.scheduled")}
            <FormattedDate date={event.publishAt} format="datetimeShort" />
          </span>
        </Badge>
      )}
      {event.externalParty && <Badge variant="special">{t("common.label.external")}</Badge>}
      {event.projectConnection && (
        <Badge key="connection-name" icon={<MarkerPin01Icon />}>
          {event.projectConnection.name}
        </Badge>
      )}
      {categoryDetails && <Badge variant="info">{getEventCategoryName(t, categoryDetails.id)}</Badge>}
    </>
  );
}
